import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Eye } from "lucide-react";
import { toast } from "sonner";
import { GiftCardApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type LogEntry = {
  id: number;
  user: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  logDate: string;
};

const GiftCardLogs: React.FC = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<LogEntry | null>(null);
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);

  // Fetch audit logs with backend pagination
  const fetchAuditLogs = useCallback(
    async (options?: { page?: number; pageSize?: number }) => {
      setIsLoading(true);
      try {
        // Get userId from sessionStorage
        const userInfo = sessionStorage.getItem("userInfo");
        const userId = userInfo ? JSON.parse(userInfo).userId : 0;

        if (!userId) {
          toast.error("User ID not found. Please login again.");
          return;
        }

        const currentPage = options?.page ?? page;
        const currentPageSize = options?.pageSize ?? rowsPerPage;

        const response = await GiftCardApi.getAllAuditLogs({
          userId: userId,
          page: currentPage + 1, // Backend expects 1-based page
          pageSize: currentPageSize,
        });

        if (response && response.logs && Array.isArray(response.logs)) {
          setLogs(response.logs);
          setTotalLogs(response.totalCount ?? response.logs.length ?? 0);
        } else {
          setLogs([]);
          setTotalLogs(0);
        }
      } catch (error) {
        console.error("Error fetching audit logs:", error);
        const errorMessage = HandleError(error);
        toast.error(errorMessage || "Failed to fetch audit logs.");
        setLogs([]);
        setTotalLogs(0);
      } finally {
        setIsLoading(false);
      }
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    fetchAuditLogs({ page: 0, pageSize: rowsPerPage });
  }, []);

  // Pagination - use backend totalCount for accurate page count
  const totalPages = Math.max(
    1,
    Math.ceil((totalLogs || 0) / (rowsPerPage || 1))
  );

  // Centralized handler for page changes (0-based index)
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage > totalPages - 1) return;
    setPage(newPage);
    fetchAuditLogs({ page: newPage, pageSize: rowsPerPage });
  };

  const columns = [
    {
      header: "ID",
      accessorKey: "id" as keyof LogEntry,
      cell: (row: LogEntry) => (
        <span className="font-medium text-gray-900">#{row.id}</span>
      ),
    },
    {
      header: "Date & Time",
      accessorKey: "logDate" as keyof LogEntry,
      cell: (row: LogEntry) => (
        <div>
          <div className="text-sm">
            {format(new Date(row.logDate), "MMM dd, yyyy")}
          </div>
          <div className="text-sm text-gray-500">
            {format(new Date(row.logDate), "HH:mm:ss")}
          </div>
        </div>
      ),
    },
    {
      header: "User",
      accessorKey: "user" as keyof LogEntry,
      cell: (row: LogEntry) => (
        <span className="text-sm text-gray-800">{row.user}</span>
      ),
    },
    {
      header: "Action",
      accessorKey: "action" as keyof LogEntry,
      cell: (row: LogEntry) => (
        <span className="text-sm font-medium">{row.action}</span>
      ),
    },
    {
      header: "IP Address",
      accessorKey: "ipAddress" as keyof LogEntry,
      cell: (row: LogEntry) => (
        <span className="text-sm">{row.ipAddress || "N/A"}</span>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row: LogEntry) => (
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3"
          onClick={() => {
            setSelected(row);
            setOpen(true);
          }}
        >
          <Eye className="h-4 w-4 mr-2" /> View
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/giftcard")}
          className="p-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Logs</h1>
          <p className="text-gray-600 mt-1">
            Review recent actions related to gift cards
          </p>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">Loading audit logs...</p>
            </div>
          ) : (
            <DataTable title="Activity Logs" data={logs} columns={columns} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex justify-between items-center">
          {/* Rows per page selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                const newRowsPerPage = parseInt(value, 10);
                setRowsPerPage(newRowsPerPage);
                setPage(0); // Reset to first page when changing rows per page
                fetchAuditLogs({
                  page: 0,
                  pageSize: newRowsPerPage,
                });
              }}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPrevHandler={() => handlePageChange(page - 1)}
              onNextHandler={() => handlePageChange(page + 1)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Log Details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">ID</span>
                <span className="col-span-2 font-medium">#{selected.id}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Date & Time</span>
                <span className="col-span-2">
                  {format(new Date(selected.logDate), "MMM dd, yyyy HH:mm:ss")}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">User</span>
                <span className="col-span-2">{selected.user}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Action</span>
                <span className="col-span-2 font-medium">
                  {selected.action}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">IP Address</span>
                <span className="col-span-2">
                  {selected.ipAddress || "N/A"}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardLogs;
